import get from 'lodash/get';
import { ObservableMap, action, computed, makeObservable, observable, reaction } from 'mobx';

import type { CMSContent } from '../../dto/cms.types';
import { getUserLanguage } from '../../i18n/helpers';
import { getRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { devError } from '../../utils/log';
import type { RootStore } from '../RootStore';

import { AUTHORIZED_PAGE_SLUG, FOOTER_ERROR, UNAUTHORIZED_PAGE_SLUG } from './constants';

export class CmsStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('cms_disabled' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    reaction(
      () => ({
        isAuthenticationFlowDone: this.rootStore.authStore.isAuthenticationFlowDone,
        authorized: this.rootStore.authStore.authorized,
      }),
      ({ isAuthenticationFlowDone, authorized }) => {
        if (isAuthenticationFlowDone && authorized) {
          this.resolveAndUpdateFlags();
        }
      },
      { fireImmediately: true },
    );

    reaction(
      () => ({
        cmsDisabled: this.cmsDisabled,
        flagsResolved: this.flagsResolved,
      }),
      ({ cmsDisabled, flagsResolved }, previous) => {
        if (flagsResolved && !cmsDisabled) {
          if (!previous || !previous.flagsResolved || previous.cmsDisabled) {
            void this.fetchLinks();
          }
        }
      },
    );
  }

  @observable
  public loading: ObservableMap<string, boolean> = observable.map([]);

  @observable
  public links: CMSContent[] = [];

  @observable
  public footer: string = '';

  @observable
  public cmsDisabled = false;

  @observable
  private flagsResolved = false;

  @computed
  public get sideMenuLinks() {
    return this.links.filter((link) => link.showInMenu);
  }

  @action
  public setLoading(key: string, loading: boolean) {
    this.loading.set(key, loading);
  }

  @action
  private setLinks(links: CMSContent[]) {
    this.links = links;
  }

  @action
  private setFooter(footerValue: string) {
    this.footer = footerValue;
  }

  @action
  private setFlagsResolved(value: boolean) {
    this.flagsResolved = value;
  }

  @action
  public setCmsDisabled(flag: boolean) {
    const previousValue = this.cmsDisabled;
    this.cmsDisabled = flag;

    if (previousValue === false && flag === true) {
      this.setFooter('');
      this.setLinks([]);
    }
  }

  public async fetchLinks(language?: string) {
    if (this.cmsDisabled) {
      return;
    }

    this.setLoading('links', true);
    try {
      const userLanguage = language || getUserLanguage();
      const response = await getRequest<ApiResponse<CMSContent[]>>('user/cms', { language: userLanguage });
      this.setLinks(get(response, 'data.data', []));
    } finally {
      this.setLoading('links', false);
    }
  }

  public async fetchFooter(language?: string) {
    if (this.cmsDisabled) {
      return;
    }

    const { authorized: isUserAuthorized } = this.rootStore.authStore;

    try {
      const pageSlug = isUserAuthorized ? AUTHORIZED_PAGE_SLUG : UNAUTHORIZED_PAGE_SLUG;
      const userLanguage = language || getUserLanguage();
      const params = { pageSlug, language: userLanguage };
      const response = await getRequest<ApiResponse<CMSContent>>('user/cms-detail', params);
      // TODO to remove the backward compatibility
      if (response.data.success) {
        const newFooter = get(response, 'data.data.content', FOOTER_ERROR) as { EN?: string; value?: string };
        const footerValue = newFooter.value || newFooter.EN || '';
        this.setFooter(footerValue);
      }
    } catch (e) {
      devError('Error while fetching footer', e);
      this.setFooter(FOOTER_ERROR);
    }
  }

  public async fetchCmsPage(pageSlug: string): Promise<CMSContent | undefined> {
    if (this.cmsDisabled) {
      return undefined;
    }

    try {
      const params = { pageSlug };
      const response = await getRequest<ApiResponse<CMSContent>>('user/cms-detail', params);

      if (response.data.success && response.data.data) {
        const cmsContent = response.data.data;
        return cmsContent;
      }
    } catch (e) {
      devError(`Error while fetching CMS page: ${pageSlug}`, e);
      return undefined;
    }
  }

  private resolveAndUpdateFlags() {
    if (!this.rootStore.authStore.authorized || !this.rootStore.userStore.profile) {
      this.setCmsDisabled(false);
      this.setFlagsResolved(true);
      return;
    }

    const cmsDisabledFlag = resolveFlag('cms_disabled', false);
    this.setCmsDisabled(cmsDisabledFlag);
    this.setFlagsResolved(true);
    void this.fetchLinks();
    void this.fetchFooter();
  }
}
